<template>
  <section class="main">
    <h1 class="intro">{{ $t("LIVE DEVICE STATUS") }}</h1>
    <p class="g-text">
      {{ $t("DEVICE_STATUS_DESC") }}
    </p>
    <p class="help">
      {{ $t("HAVING_TROUBLES") }}

      <span class="help__buttons">
        <button
          class="help__button g-button"
          :class="{ 'g-button_disabled': clearing }"
          @click="clear"
        >
          <loader v-show="clearing" size="15px" border="3px" absolute />
          {{ $t("EMPTY_COMMAND_QUEUE") }}
        </button>
        &nbsp;
        <button class="help__button g-button" @click="updateProfile">
          {{ $t("UPDATE PROFILE") }}
        </button>
        &nbsp;
        <a
          class="help__button g-button"
          href="https://forum.appdb.to/index.php?/topic/4961-read-first-troubleshooting-app-installations-and-crashes/"
        >
          {{ $t("TROUBLESHOOTING") }}
        </a>
        &nbsp;
        <button
          v-if="config.is_pro === 'yes'"
          class="help__button g-button"
          :class="{ 'g-button_disabled': recovering }"
          @click.prevent="recoverApps"
        >
          <loader v-show="recovering" size="15px" border="3px" absolute />
          {{ $t("RECOVER_APPS_IF_UNAVAILABLE") }}
        </button>
      </span>
    </p>
    <div v-if="!isMobile" class="table-wrapper">
      <table class="table">
        <thead class="table__head table__row">
          <tr>
            <th class="table__cell">{{ $t("COMMAND TYPE") }}</th>
            <th class="table__cell">{{ $t("COMMAND RESULT") }}</th>
            <th class="table__cell">{{ $t("PURPOSE") }}</th>
            <th class="table__cell">{{ $t("DETAILS") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr class="table__row" v-for="row in history" :key="row.uuid">
            <td class="table__cell">
              <span class="table__date">
                {{ getDate(row.added) }}
              </span>
              {{ $t(row.type.toUpperCase()) }}
            </td>
            <td class="table__cell table__cell_nw">
              <span class="table__date">
                {{ getDate(row.acknowledged) }}
              </span>
              <span
                class="table__status g-status"
                :class="{
                  'g-status_success': /^ok/i.test(row.status),
                  'g-status_error': /error|failed/i.test(row.status),
                }"
              >
                {{ $t(row.status.toUpperCase(), row.status) }}
              </span>
              <button
                class="table__button g-button"
                :class="{ 'g-button_disabled': row.pending }"
                v-if="canRetry(row)"
                @click="retry(row.uuid)"
              >
                <loader
                  v-show="row.pending"
                  size="13px"
                  border="3px"
                  absolute
                />
                {{ $t("RETRY") }}
              </button>
              <button
                class="table__button g-button"
                :class="{ 'g-button_disabled': row.pending }"
                v-if="canFix(row)"
                @click="fix(row.uuid)"
              >
                <loader
                  v-show="row.pending"
                  size="13px"
                  border="3px"
                  absolute
                />
                {{ $t("FIX") }}
              </button>
            </td>
            <td class="table__cell">{{ row.params.purpose }}</td>
            <td class="table__cell" v-html="getDetails(row.params)"></td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="commands">
      <div class="command" v-for="command in history" :key="command.uuid">
        <span class="command__heading">{{ $t("COMMAND TYPE") }}</span>
        <span class="command__date">
          {{ getDate(command.added) }}
        </span>
        {{ $t(command.type.toUpperCase()) }}
        <span class="command__heading">{{ $t("COMMAND RESULT") }}</span>
        <span class="command__date">
          {{ getDate(command.acknowledged) }}
        </span>
        <span
          class="command__status g-status"
          :class="{
            'g-status_success': /^ok/i.test(command.status),
            'g-status_error': /error|failed/i.test(command.status),
          }"
        >
          {{ $t(command.status.toUpperCase(), command.status) }}
        </span>
        <button
          class="command__button g-button"
          :class="{ 'g-button_disabled': command.pending }"
          v-if="canRetry(command)"
          @click="retry(command.uuid)"
        >
          <loader v-show="command.pending" size="13px" border="3px" absolute />
          {{ $t("RETRY") }}
        </button>
        <button
          class="command__button g-button"
          :class="{ 'g-button_disabled': command.pending }"
          v-if="canFix(command)"
          @click="fix(command.uuid)"
        >
          <loader v-show="command.pending" size="13px" border="3px" absolute />
          {{ $t("FIX") }}
        </button>
        <span class="command__heading">{{ $t("PURPOSE") }}</span>
        <span>{{ command.params.purpose }}</span>
        <template v-if="getDetails(command.params)">
          <span class="command__heading">{{ $t("DETAILS") }}</span>
          <span v-html="getDetails(command.params)"></span>
        </template>
      </div>
    </div>
  </section>
</template>

<script>
import Loader from '@/components/Loader';
import { mapState } from 'vuex';

export default {
  components: { Loader },
  data() {
    return {
      isMobile: window.innerWidth <= 767 || window.innerHeight <= 767,
      clearing: false,
      recovering: false,
      history: [],
      interval: null,
    };
  },
  computed: {
    title() {
      return this.$t('LIVE DEVICE STATUS');
    },
    ...mapState(['config']),
  },
  methods: {
    retry(uuid) {
      const row = this.history.find(r => r.uuid === uuid);
      row.pending = true;
      this.$api.retryCommand(uuid)
        .then(this.getStatus)
        .finally(() => {
          row.pending = false;
        });
    },
    fix(uuid) {
      const row = this.history.find(r => r.uuid === uuid);
      row.pending = true;
      this.$api.fixCommand(uuid)
        .then(this.getStatus)
        .finally(() => {
          row.pending = false;
        });
    },
    clear() {
      this.clearing = true;
      this.$api.clearCommands().then(this.getStatus).finally(() => { this.clearing = false; });
    },
    async updateProfile() {
      const agree = await this.$confirm(this.$t('UPDATE_PROFILE_CONFIRM')).catch(() => false);
      if (agree) {
        window.location = `https://dbservices.to/enroll/?lt=${this.$store.getters.lt}`;
      }
    },
    getStatus() {
      return this.$api.getDeviceStatus().then((status) => {
        this.history = status.map((row) => {
          const newRow = { ...row, pending: false };
          try {
            newRow.params = JSON.parse(row.params) || {};
          } catch {
            newRow.params = {};
          }
          return newRow;
        });
      });
    },
    canRetry(row) {
      return /^(ok|failed)/i.test(row.status) && (row.type === 'install_app' || row.type === 'install_media');
    },
    canFix(row) {
      return this.canRetry(row) && /fixable/i.test(row.status);
    },
    getDetails(params) {
      if (params.sign) {
        return `${params.sign.status}<br>${params.sign.status_text}`;
      }
      if (params.error) {
        return params.error;
      }
      return '';
    },
    recoverApps() {
      this.recovering = true;
      this.$api.saveConfiguration({
        send_pro_provisions: 1,
      }).finally(() => {
        this.recovering = false;
      });
    },
    getDate(stamp) {
      if (!+stamp) {
        return this.$t('NA');
      }

      const date = new Date(stamp * 1000);
      return `${date.toLocaleDateString()}, ${date.toLocaleTimeString()}`;
    },
  },
  created() {
    this.getStatus();

    this.interval = setInterval(this.getStatus, 1 * 1000);
  },
  destroyed() {
    clearInterval(this.interval);
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
  display: block;
}

.help {
  line-height: 28px;
  margin-top: 8px;

  &__buttons {
    display: block;
  }

  &__button {
    font-weight: 400;
    line-height: normal;
    font-family: inherit;

    @include tablet {
      margin-bottom: 8px;
    }
  }
}

.table-wrapper {
  @include notebook {
    overflow-x: auto;
    width: 100vw;
    margin-left: -8px;
  }
}

.commands {
  margin-top: 15px;
}

.command {
  padding: 13px 0;
  border-bottom: 1px solid #e5e5e5;

  &:first-child {
    border-top: 1px solid #e5e5e5;
  }

  &__heading {
    font-size: 15px;
    font-weight: 500;
    display: block;
    margin-bottom: 5px;

    &:not(:first-child) {
      margin-top: 10px;
    }
  }

  &__date {
    display: block;
    white-space: nowrap;
    color: $text-secondary;
    font-size: 12px;
    margin-bottom: 5px;
  }
}

.table__date,
.command__date {
  display: block;
  white-space: nowrap;
  color: $text-secondary;
  font-size: 12px;
  margin-bottom: 5px;
}

.table__status,
.command__status {
  font-size: 14px;
  white-space: nowrap;
}

.table__button,
.command__button {
  padding-top: 0px;
  padding-bottom: 1px;
  margin-left: 5px;
}

.table {
  margin-top: 15px;
  text-align: left;
  width: 100%;

  &__head {
    font-weight: 500;
  }

  &__cell {
    padding: 8px;

    &_nw {
      white-space: nowrap;
    }
  }

  &__row {
    border-bottom: 1px solid #e5e5e5;

    &:hover {
      background-color: $lighter-primary;
    }
  }
}
</style>
